import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackIcon,
  ArrowSquareOut as OpenInNewIcon,
  ChartLine as AnalyticsIcon,
  Copy as CopyIcon,
  Globe as DomainIcon,
  MagnifyingGlass as SearchIcon,
  Phone as PhoneIcon,
} from '@phosphor-icons/react';
import api from '../../../lib/api';
import { CustomDomainPanel } from './CustomDomainPanel';
import type { WebsiteProjectDetail, WebsiteProjectUpdatePayload } from '../../../types/websiteBuilder';

const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, children }) => (
  <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'hidden' }}>
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {icon}
        <Typography level="title-lg">{title}</Typography>
      </Box>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2, maxWidth: 640 }}>
        {subtitle}
      </Typography>
      {children}
    </Box>
  </Sheet>
);

const WebsiteGlobalSettings: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [gaId, setGaId] = useState('');
  const [verification, setVerification] = useState('');
  const [phoneOverride, setPhoneOverride] = useState('');
  const [ctaOverride, setCtaOverride] = useState('');

  const { data: project, isLoading, isError, refetch } = useQuery<WebsiteProjectDetail>({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => (await api.websiteBuilder.getProject(projectId!)).data,
    enabled: !!projectId,
  });

  const serverValues = useRef({ id: 0, gaId: '', verification: '', phone: '', cta: '' });
  useEffect(() => {
    if (!project) return;
    const old = serverValues.current;
    const next = { id: project.id, gaId: project.google_analytics_id || '', verification: project.search_console_verification || '', phone: project.business_overrides?.phone || '', cta: project.business_overrides?.cta_text || '' };
    const replace = (current: string, before: string, after: string) => old.id !== next.id || current === before ? after : current;
    setGaId(current => replace(current, old.gaId, next.gaId));
    setVerification(current => replace(current, old.verification, next.verification));
    setPhoneOverride(current => replace(current, old.phone, next.phone));
    setCtaOverride(current => replace(current, old.cta, next.cta));
    serverValues.current = next;
  }, [project]);

  const hasDraft = !!project && (gaId !== (project.google_analytics_id || '') || verification !== (project.search_console_verification || '') || phoneOverride !== (project.business_overrides?.phone || '') || ctaOverride !== (project.business_overrides?.cta_text || ''));
  useEffect(() => {
    if (!hasDraft) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasDraft]);

  const saveIntegrations = useMutation({
    mutationFn: async (payload: WebsiteProjectUpdatePayload) =>
      (await api.websiteBuilder.updateProject(projectId!, payload)).data,
    onSuccess: (data, payload) => {
      if (payload.google_analytics_id !== undefined) setGaId(current => current === payload.google_analytics_id ? data.google_analytics_id || '' : current);
      if (payload.search_console_verification !== undefined) setVerification(current => current === payload.search_console_verification ? data.search_console_verification || '' : current);
      if (payload.business_overrides) {
        setPhoneOverride(current => current.trim() === payload.business_overrides?.phone ? data.business_overrides?.phone || '' : current);
        setCtaOverride(current => current.trim() === payload.business_overrides?.cta_text ? data.business_overrides?.cta_text || '' : current);
      }
      toast.success('Saved — changes go live on your next Deploy');
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
    },
    onError: (error) => {
      const data = (error as { responseData?: Record<string, string[] | string> })?.responseData;
      const first = data && Object.values(data)[0];
      toast.error(
        (Array.isArray(first) ? first[0] : typeof first === 'string' ? first : '') ||
        'Could not save settings',
      );
    },
  });

  const copy = (value: string) => {
    navigator.clipboard?.writeText(value);
    toast.success('Copied');
  };

  if (isError) return <Alert color="danger">Could not load settings. Your saved website is unchanged.<Button onClick={() => refetch()}>Retry</Button></Alert>;
  if (isLoading || !project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const siteBase = (project.production_url || '').replace(/\/$/, '');
  const sitemapUrl = siteBase ? `${siteBase}/sitemap.xml` : '';
  const gaDirty = gaId !== (project.google_analytics_id || '');
  const verificationDirty = verification !== (project.search_console_verification || '');
  const businessDirty =
    phoneOverride !== (project.business_overrides?.phone || '') ||
    ctaOverride !== (project.business_overrides?.cta_text || '');

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <IconButton
          variant="plain"
          size="sm"
          onClick={() => { if (!hasDraft || window.confirm('Discard unsaved settings and return to the site manager?')) navigate(`/marketing/website-builder/${projectId}/manage`); }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography level="h2">Global Settings</Typography>
        <Chip size="sm" variant="soft" sx={{ ml: 0.5 }}>{project.name}</Chip>
      </Box>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3, ml: 5 }}>
        Site-wide settings that apply to every page of your website.
      </Typography>

      <Stack spacing={3}>
        <SectionCard
          icon={<DomainIcon size={22} />}
          title="Custom Domain"
          subtitle="Serve your website from your own domain with automatic SSL."
        >
          {/* The panel renders its own heading context; embed body-only */}
          <Box sx={{ mx: { xs: -2, md: -3 }, mt: -2 }}>
            <CustomDomainPanel project={project} projectId={projectId!} />
          </Box>
        </SectionCard>

        <SectionCard
          icon={<PhoneIcon size={22} />}
          title="Business Info"
          subtitle="The phone number and call-to-action shown across the whole site. They follow your Organization Settings automatically — set an override only if this site should use a different number (e.g. call tracking)."
        >
          <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography level="body-sm">
                Currently on this site: <strong>{project.live_business_info?.phone || 'No phone set'}</strong>
              </Typography>
              {!project.business_overrides?.phone && (
                <Button
                  variant="plain"
                  size="sm"
                  endDecorator={<OpenInNewIcon />}
                  onClick={() => window.open('/settings/organization', '_blank')}
                >
                  From Organization Settings
                </Button>
              )}
            </Box>
            {project.status === 'deployed' && project.business_in_sync === false && (
              <Chip color="warning" variant="soft" size="sm" sx={{ alignSelf: 'flex-start' }}>
                The live site still shows an older number — use “Update &amp; Publish” in the site manager
              </Chip>
            )}
            <FormControl>
              <FormLabel>Phone override (optional)</FormLabel>
              <Input
                value={phoneOverride}
                onChange={(e) => setPhoneOverride(e.target.value)}
                placeholder="(555) 123-4567 — leave empty to use your organization phone"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Call-to-action text (optional)</FormLabel>
              <Input
                value={ctaOverride}
                onChange={(e) => setCtaOverride(e.target.value)}
                placeholder="Get Free Estimate"
              />
            </FormControl>
            <Box>
              <Button
                disabled={!businessDirty}
                loading={saveIntegrations.isPending}
                onClick={() =>
                  saveIntegrations.mutate({
                    business_overrides: {
                      ...(project.business_overrides || {}),
                      phone: phoneOverride.trim(),
                      cta_text: ctaOverride.trim(),
                    },
                  })
                }
              >
                Save Business Info
              </Button>
              {businessDirty && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  Saving updates your draft and preview automatically; publish from the site manager
                  to update the live site.
                </Typography>
              )}
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          icon={<AnalyticsIcon size={22} />}
          title="Google Analytics"
          subtitle="Add your GA4 measurement ID and every page reports visits, traffic sources, and conversions to your Analytics property."
        >
          <Stack spacing={1.5} sx={{ maxWidth: 480 }}>
            <FormControl>
              <FormLabel>Measurement ID</FormLabel>
              <Input
                value={gaId}
                onChange={(e) => setGaId(e.target.value.trim())}
                placeholder="G-XXXXXXXXXX"
              />
              <FormHelperText>
                Google Analytics → Admin → Data streams → your web stream. Leave empty to disable.
              </FormHelperText>
            </FormControl>
            <Box>
              <Button
                disabled={!gaDirty}
                loading={saveIntegrations.isPending}
                onClick={() => saveIntegrations.mutate({ google_analytics_id: gaId })}
              >
                Save Analytics
              </Button>
              {gaDirty && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  Applies to every page on your next Deploy (or Publish This Page).
                </Typography>
              )}
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          icon={<SearchIcon size={22} />}
          title="Google Search Console"
          subtitle="Verify ownership of your site and submit your sitemap so Google indexes every page."
        >
          <Stack spacing={2}>
            <FormControl sx={{ maxWidth: 560 }}>
              <FormLabel>Verification token</FormLabel>
              <Input
                value={verification}
                onChange={(e) => setVerification(e.target.value)}
                placeholder='Paste the token or the full <meta name="google-site-verification" …> tag'
              />
              <FormHelperText>
                Search Console → Add property → URL prefix → HTML tag method. Paste either the
                whole tag or just its content value.
              </FormHelperText>
            </FormControl>
            <Box>
              <Button
                disabled={!verificationDirty}
                loading={saveIntegrations.isPending}
                onClick={() => saveIntegrations.mutate({ search_console_verification: verification })}
              >
                Save Verification
              </Button>
            </Box>

            <Divider />

            <Box>
              <Typography level="title-sm" sx={{ mb: 0.5 }}>Your sitemap</Typography>
              {sitemapUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography level="body-sm" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {sitemapUrl}
                  </Typography>
                  <IconButton size="sm" variant="plain" title="Copy" onClick={() => copy(sitemapUrl)}>
                    <CopyIcon />
                  </IconButton>
                  <IconButton
                    size="sm" variant="plain" title="Open"
                    onClick={() => window.open(sitemapUrl, '_blank')}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Box>
              ) : (
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  Deploy your website first — the sitemap is generated automatically at /sitemap.xml.
                </Typography>
              )}
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                Every deploy regenerates the sitemap with all of your pages (search engines are also
                pointed at it via robots.txt). To connect: 1) save the verification token above,
                2) Deploy your site, 3) click Verify in Search Console, then 4) submit the sitemap
                URL under Sitemaps.
              </Typography>
              <Button
                variant="plain"
                size="sm"
                endDecorator={<OpenInNewIcon />}
                sx={{ mt: 0.5, ml: -1 }}
                onClick={() => window.open('https://search.google.com/search-console', '_blank')}
              >
                Open Google Search Console
              </Button>
            </Box>
          </Stack>
        </SectionCard>
      </Stack>
    </Box>
  );
};

export default WebsiteGlobalSettings;
